/* eslint-disable no-restricted-syntax */

/* eslint-disable import/no-extraneous-dependencies */

/* storybook-check-ignore */
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';

import { injectGlobal } from '@emotion/css';
import { Box, Button } from '@opendoor/bricks/core';

injectGlobal`
  .useCarousel-inner-box {
    width: var(--scrollable-width-300);
  }
  @container (min-width: 700px) {
    .useCarousel-inner-box {
      width: var(--scrollable-width-700);
    }
  }
  @container (min-width: 900px) {
    .useCarousel-inner-box {
      width: var(--scrollable-width-900);
    }
  }
  @container (min-width: 1200px) {
    .useCarousel-inner-box {
      width: var(--scrollable-width-1200);
    }
  }
`;

/**
 * A hook that returns a carousel component and a scroll function to scroll the carousel
 *
 * @param columns - The columns to be displayed in the carousel. They must be block elements
 * and/or width=100% so that they fill their container
 */
export function useCarousel(columns: React.ReactNode[]) {
  const scrollBoxRef = useRef<HTMLDivElement>(null);
  const innerBoxRef = useRef<HTMLDivElement>(null);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [dotsCount, setDotsCount] = useState(0);

  const scroll = useCallback(
    (directionOrPosition: 'left' | 'right' | number) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const scrollBox = scrollBoxRef.current!;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const innerBox = innerBoxRef.current!;

      const cardWidth = Math.round(innerBox.clientWidth / columns.length);

      let left = 0;

      if (typeof directionOrPosition === 'number') {
        left = (directionOrPosition - currentPosition) * cardWidth;
      } else {
        left = (directionOrPosition === 'left' ? -1 : 1) * cardWidth;
      }

      scrollBox.scrollBy({ left, behavior: 'smooth' });
    },
    [columns.length, currentPosition],
  );

  useEffect(function watchIfAllCardsAreVisible() {
    // check if all cards are visible, if so, hide the dots
    if (!scrollBoxRef.current) return;
    const scrollBox = scrollBoxRef.current;
    if (!innerBoxRef.current) return;
    const innerBox = innerBoxRef.current;

    const resizeObserver = new ResizeObserver(() => {
      setDotsCount(Math.ceil(innerBox.clientWidth / scrollBox.clientWidth));
    });

    resizeObserver.observe(scrollBox);
    return () => {
      resizeObserver.unobserve(scrollBox);
    };
  }, []);

  useEffect(
    function watchScroll() {
      if (!innerBoxRef.current) return;
      const innerBox = innerBoxRef.current;
      if (!scrollBoxRef.current) return;
      const scrollBox = scrollBoxRef.current;

      const scrollEventListener = () => {
        const cardWidth = Math.round(innerBox.clientWidth / columns.length);
        setCurrentPosition(Math.ceil(scrollBox.scrollLeft / cardWidth));
      };

      scrollBox.addEventListener('scroll', scrollEventListener);
      return () => {
        scrollBox.removeEventListener('scroll', scrollEventListener);
      };
    },
    [columns, innerBoxRef, scrollBoxRef],
  );

  return {
    jsx: (
      <>
        <Box
          overflowX="scroll"
          mb={4}
          ref={scrollBoxRef}
          sx={{
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            '-ms-overflow-style': 'none',
            'scrollbar-width': 'none',
            'container-type': 'inline-size',
          }}
        >
          <Box
            ref={innerBoxRef}
            className="useCarousel-inner-box"
            sx={
              {
                '--scrollable-width-300': columns.length * (columns.length > 1 ? 90 : 100) + '%',
                '--scrollable-width-700':
                  columns.length * (columns.length > 2 ? 46 : 100 / columns.length) + '%',
                '--scrollable-width-900':
                  columns.length * (columns.length > 3 ? 32 : 100 / columns.length) + '%',
                '--scrollable-width-1200':
                  columns.length * (columns.length > 4 ? 24.5 : 100 / columns.length) + '%',
              } as CSSProperties
            }
          >
            <Box display="grid" gridTemplateColumns={`repeat(${columns.length}, 1fr)`} gridGap={3}>
              {columns.map((card) => card)}
            </Box>
          </Box>
        </Box>
        <Box textAlign="center" visibility={dotsCount > 1 ? 'visible' : 'hidden'}>
          {Array(dotsCount)
            .fill(0)
            .map((_, i) => (
              <NavDot isActive={currentPosition === i} key={i} onClick={() => scroll(i)} />
            ))}
        </Box>
      </>
    ),
    scroll,
  };
}

/**
 * A dot icon which color is defaulted to currentColor
 *
 * Compared to ascii dot, is sized and layouted better. Recommended to be used inline with text.
 */
function Dot({ fill = 'currentColor' }) {
  return (
    <svg style={{ margin: '0 4px 1px 0' }} height={8} width={7} viewBox="0 0 50 50">
      <circle fill={fill} cx="25" cy="25" r="25"></circle>
    </svg>
  );
}

/**
 * A button that is visualized as a dot for scroll indication and navigation
 *
 * Is useful for navigatable components like a carousel
 */
function NavDot({ isActive, onClick }: { isActive: boolean; onClick: () => void }) {
  return (
    <Button
      analyticsName="scroll-dot"
      aria-label="scroll-dot"
      display="inline"
      onClick={onClick}
      type="button"
      variant="ghost"
    >
      <Dot fill={isActive ? 'var(--colors-warmgrey950)' : 'var(--colors-warmgrey300)'} />
    </Button>
  );
}
